import Analytics from '@opencare/analytics';
import React, { useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Modal } from 'common/components/Modal/Modal';

import { ModalHeader, ModalSubtitle, ModalTitle } from 'common/components/Modal/Modal-styled-components';

import { updateSelectedAppointment } from 'connect/actions';
import { selectDataClinic } from 'connect/selectors';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState } from 'pages/checkout/connect/types';

import { filterInNetworkProviders } from 'pages/profile/helpers/profile-helpers';

import { Clinic } from 'types/Clinic';
import { useEffectOnce } from 'utils/customHooks/useEffectOnce';
import Logger from 'utils/Logger';

import { NoAvailabilityModalBody, StyledInstantBookView } from './NoAvailabilityModal-styled-components';

const analytics = new Analytics();
const modalAnalyticsConstants = {
  pageType: analytics.pageTypes.CHECKOUT,
  pageName: 'checkout-confirm',
  name: 'no-availability-modal',
  section: 'no-availability-modal',
};

interface NoAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const NoAvailabilityModal = ({
  isOpen,
  onClose,
}: NoAvailabilityModalProps) => {
  const dispatch = useDispatch();

  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const insurancePlan = checkoutState?.insurance?.plan;

  const clinic: Clinic = useSelector(selectDataClinic);

  // Slots from in-network providers
  const availableSlots = useMemo(() => {
    // If insurancePlan is falsey, use all slots
    // This matches the behaviour used by filterInNetworkProviders
    if (!insurancePlan?.id) {
      return clinic.slots;
    }

    const inNetworkProviders = filterInNetworkProviders(clinic.hours, insurancePlan?.id, clinic.providers);
    const inNetworkProviderIds = inNetworkProviders.map((provider) => provider.id);

    return clinic.slots.filter(
      (slot) => {
        const providerId = typeof slot.provider === 'number' ? slot.provider : slot.provider.id;
        return inNetworkProviderIds.includes(providerId);
      },
    );
  }, [clinic.hours, clinic.providers, clinic.slots, insurancePlan?.id]);

  const handleSelectSlot = useCallback((slotId: number) => {
    if (!clinic.providers?.length) {
      Logger.error('NoAvailabilityModal: No providers found, cannot update timeslot.', { providers: clinic.providers });
      return;
    }

    const requestedProviderId = clinic.slots.find((slot) => slot.id === slotId)?.provider;
    const requestedProvider = clinic.providers.find((provider) => provider.id === requestedProviderId);

    dispatch(
      updateSelectedAppointment(
        requestedProviderId,
        slotId,
        requestedProvider,
        true, // areAllProvidersSelected,
      ),
    );

    analytics.trackClick({
      ...modalAnalyticsConstants,
      customProperties: {
        slot_id: String(slotId),
      },
    });

    onClose();
  }, [onClose, clinic.slots, clinic.providers, dispatch]);

  useEffectOnce(() => {
    analytics.trackPageView(modalAnalyticsConstants);
  });

  return (
    <Modal
      visible={isOpen}
      onClose={onClose}
    >
      <NoAvailabilityModalBody>
        <ModalHeader>
          <ModalTitle>
            Your selected time is no longer available
          </ModalTitle>
          <ModalSubtitle>
            Please choose another available slot to continue your appointment booking.
          </ModalSubtitle>
        </ModalHeader>

        <StyledInstantBookView
          allSlots={availableSlots}
          onRequestAppointment={handleSelectSlot}
          numDaysToDisplay={7}
          showDateRange
          showSelectedDate
        />
      </NoAvailabilityModalBody>
    </Modal>
  );
};
